import React, { useEffect, useState } from 'react'
import Head from '../../layout/head/Head'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  Avatar,
  Button,
  CardContent,
  Divider,
  Radio,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Container,
  useMediaQuery,
  useTheme
} from '@mui/material'

import { PaymentServices } from '../../services/McDataServices'
import { Icon } from '../../components/Component'
import { useGlobalStoreData } from '../../store/globalStore'
import Loading from '../../components/loader/Loading'
import { isArray } from 'lodash'

const tableCellStyles = { padding: '2px 10px' }

const getCurrencySymbol = (currency) => {
  let symbol = currency

  switch (currency) {
    case 'USD':
      symbol = '$'
      break
    case 'EUR':
      symbol = '€'
      break
    case 'TRY':
      symbol = '₺'
      break

    default:
      break
  }

  return symbol
}

const PaymentMethodOption = ({ isActive }) => {
  return (
    <Paper
      elevation={1}
      sx={{
        padding: 1,
        borderRadius: 2,
        boxShadow: isActive
          ? '0px 0px 5px 0px var(--mui-palette-success-main)'
          : '0px 0px 5px 0px var(--mui-palette-divider)',
        // border: '2px solid',
        // borderColor: 'success.main',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Radio
        checked
        value="credit-card"
        name="payment-method"
        color="success"
      />
      <Avatar
        sx={{
          width: 45,
          height: 45,
          backgroundColor: 'action.hover'
        }}
      >
        <img
          src="https://www.tap.company/_next/image?url=https%3A%2F%2Fwebsiteimages.b-cdn.net%2FLogo_7de102aab6.svg&w=640&q=75"
          alt="visa"
          width="34"
        />
      </Avatar>
      <Typography variant="body1" fontWeight="medium">
        TAP - GbClubs
      </Typography>
    </Paper>
  )
}

const getManagedCountByParameterType = (item) => {
  const dividedByTenList = ['MARKET_REPORT_TOTAL_SEARCH_COUNT', 'MARKET_REPORT_DAILY_SEARCH_COUNT', 'PRODUCT_DETAIL_SEARCH_COUNT', 'B2B_PRODUCT_DETAIL_SEARCH_COUNT']
  if (dividedByTenList.includes(item.type)) {
    return item.parameters.count / 10
  } else {
    return item.parameters.count
  }
}

export const generateParameters = (item, countriesGSD) => {
  if (Object.prototype.hasOwnProperty.call(item.parameters, 'count')) {
    return (
      <Chip
        label={getManagedCountByParameterType(item)}
        color="success"
        size="small"
        variant="outlined"
        className="m-1"
        sx={{
          color: 'var(--mui-palette-success-light)',
          borderColor: 'var(--mui-palette-divider)'
        }}
      />
    )
  }

  if (Object.prototype.hasOwnProperty.call(item.parameters, 'allowedHsCodes')) {
    if (!isArray(item.parameters?.allowedHsCodes)) {
      return item.parameters?.allowedHsCodes?.split(',').map((code, index) => (
      <Chip
        key={index}
        label={code}
        color="success"
        size="small"
        variant="outlined"
        className="m-1"
        sx={{
          color: 'var(--mui-palette-success-light)',
          borderColor: 'var(--mui-palette-divider)'
        }}
      />
      ))
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(item.parameters, 'allowedSearchCountries')
  ) {
    if (!isArray(item.parameters?.allowedSearchCountries)) {
      return item.parameters?.allowedSearchCountries?.split(',').map((code, index) => {
        const found = countriesGSD.find(
          (country) => country.countryCode === code
        )
        return (
          <Chip
            key={index}
            label={found.countryName}
            color="success"
            size="small"
            variant="outlined"
            className="m-1"
            sx={{
              color: 'var(--mui-palette-success-light)',
              borderColor: 'var(--mui-palette-divider)'
            }}
          />
        )
      })
    }
  }
}

const getConvertedRuleData = (data = []) => {
  const convertedData = {
    b2b: [],
    mc: []
  }

  data.forEach((rule) => {
    if (rule.type.includes('B2B_')) {
      convertedData.b2b.push(rule)
    } else {
      convertedData.mc.push(rule)
    }
  })

  return convertedData
}

const generateTableData = (convertedData) => {
  const tableData = []

  const tableDataRowCount = Math.max(
    convertedData?.b2b?.length,
    convertedData?.mc?.length
  )

  for (let index = 0; index < tableDataRowCount; index++) {
    tableData.push({
      mc: convertedData.mc?.[index],
      b2b: convertedData.b2b?.[index]
    })
  }

  return tableData
}

const TableCellGroup = ({ item, countriesGSD, t }) => {
  return (
    <>
      <TableCell style={tableCellStyles}>
        {item?.type && (
          <Typography
            variant="body2"
            fontWeight="medium"
          >
            {t(`payment.rules.${item.type}`)}
          </Typography>
        )}
      </TableCell>
      <TableCell
        align="right"
        style={tableCellStyles}
      >
        {item?.parameters && (
          <div>
            {generateParameters(item, countriesGSD)}
          </div>
        )}
      </TableCell>
    </>
  )
}

const Payment = ({ userRoles }) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState({})
  const [loading, setLoading] = useState(false)
  const countriesGSD = useGlobalStoreData.countries()
  const theme = useTheme()
  const breakpointsDownSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const handlePayment = () => {
    const payload = {
      paymentLinkId: state?.linkId,
      paymentSystem: 'TAP'
    }
    setLoading(true)
    PaymentServices.BuyPackage(payload).then(
      (redirectURL) => (window.location.href = redirectURL)
    ).finally(() => {
      setLoading(false)
    })
  }

  const getPackageDetails = () => {
    PaymentServices.PackageDetails(searchParams.get('linkId')).then(
      (response) => setState(response)
    )
  }

  useEffect(getPackageDetails, [])

  const convertedRuleData = getConvertedRuleData(state?.package?.rules)
  const tableData = generateTableData(convertedRuleData)

  const currencySymbol = state?.package?.currency ? getCurrencySymbol(state.package.currency) : ''

  return (
    <React.Fragment>
      <Head title={t('payment.header')} />
      {loading ? <Loading /> : ''}
      <Container maxWidth="xl">
        {/* eslint-disable-next-line multiline-ternary */}
        {state?.linkId ? (
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              boxShadow: '0px 0px 5px 0px var(--mui-palette-divider)'
            }}
          >
            <Grid container>
              <Grid item xs={12} md={8}>
                <CardContent
                  sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <Typography variant="h5" fontWeight="bold">
                    {t('payment.paymentMethod')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <PaymentMethodOption isActive={true} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h5" fontWeight="bold">
                        {t('payment.package')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('payment.subHeader')}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      {breakpointsDownSmall
                        ? <>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell style={tableCellStyles} colSpan={2}>
                                    <Typography variant="h6" fontWeight="bold">
                                      {state?.package?.name}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {convertedRuleData?.mc?.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCellGroup
                                      item={item}
                                      countriesGSD={countriesGSD}
                                      t={t}
                                    />
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <Table className='mt-2'>
                              <TableHead>
                                <TableRow>
                                  <TableCell style={tableCellStyles} colSpan={2}>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {convertedRuleData?.b2b?.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCellGroup
                                      item={item}
                                      countriesGSD={countriesGSD}
                                      t={t}
                                    />
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </>
                        : <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell style={tableCellStyles} colSpan={4}>
                                  <Typography variant="h6" fontWeight="bold">
                                    {state?.package?.name}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tableData.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCellGroup
                                    item={item?.mc}
                                    countriesGSD={countriesGSD}
                                    t={t}
                                  />
                                  <TableCellGroup
                                    item={item?.b2b}
                                    countriesGSD={countriesGSD}
                                    t={t}
                                  />
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                      }
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
              <Divider orientation="vertical" flexItem sx={{ mr: '-1px' }} />
              <Grid item xs={12} md={4}>
                <CardContent
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <Typography variant="h4" fontWeight="bold">
                    {t('payment.orderSummary')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment.orderSummarySub')}
                  </Typography>
                  <Paper
                    elevation={1}
                    sx={{
                      padding: 3,
                      backgroundColor: 'action.hover',
                      borderRadius: 2,
                      boxShadow:
                        '0px 0px 5px 0px var(--mui-palette-text-secondary)'
                    }}
                  >
                    <Typography variant="body2">
                      {t('payment.orderSummaryPriceText')}
                    </Typography>
                    <Typography variant="h1" fontWeight="bold" color="text.secondary">
                      {currencySymbol}
                      {state?.package?.price}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: '0.75rem'
                      }}
                    >
                      <b>{t('payment.validFor')}</b> {state?.package?.durationYear !== 0 ? `${state?.package?.durationYear} ${t('payment.year')} ${state?.package?.durationMonth} ${t('payment.month')}` : `${state?.package?.durationMonth} ${t('payment.month')}`}
                    </Typography>
                  </Paper>
                  <Divider />
                  {/* <div>
                    <div className="flex justify-between">
                      <Typography variant="body1">Subscription</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {`${currencySymbol}85.99`}
                      </Typography>
                    </div>
                    <div className="flex justify-between">
                      <Typography variant="body1">Tax</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {`${currencySymbol}4.99`}
                      </Typography>
                    </div>
                    <Divider sx={{ my: 2 }} />
                    <div className="flex justify-between">
                      <Typography variant="body1">Total</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {`${currencySymbol}90.98`}
                      </Typography>
                    </div>
                  </div> */}
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    endIcon={<Icon name="arrow-right-circle-fill" />}
                    onClick={handlePayment}
                    style={{
                      boxShadow: '0px 0px 5px 0px var(--mui-palette-divider)'
                    }}
                  >
                    {t('payment.pay')}
                  </Button>
                  {/* <Typography variant="body2" color="text.secondary">
                    {t('payment.terms')}
                  </Typography> */}
                </CardContent>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <>{t('payment.error')}</>
        )}
      </Container>
    </React.Fragment>
  )
}

export default Payment
